import React, { useMemo, useState } from 'react';
import { ExternalLinkIcon, SearchIcon } from 'lucide-react';
import { LineItem, Role } from '../types/commission';
import { CommissionTotal, formatBps, formatCurrency, formatDate } from '../utils/commission';
interface LoanBreakdownTableProps {
  role: Role;
  items: LineItem[];
  heading: string;
  subtitle: string | null;
  totals: CommissionTotal;
  /** Admin-only BPS column. */
  showBpsColumn: boolean;
  canEditBps: boolean;
  onEditBps: (item: LineItem) => void;
  /** Hides interactive controls while the page is captured for PDF export. */
  hideControls?: boolean;
}
type SortKey = 'fundedDate' | 'loanAmount' | 'bps' | 'commissionPercentage' | 'commission';
const PAGE_SIZE = 10;
export function LoanBreakdownTable({
  role,
  items,
  heading,
  subtitle,
  totals,
  showBpsColumn,
  canEditBps,
  onEditBps,
  hideControls = false
}: LoanBreakdownTableProps) {
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('fundedDate');
  const [descending, setDescending] = useState(false);
  const [page, setPage] = useState(0);
  const showCommissionPercentageColumn = items.some(item => item.loan.commissionPercentage !== 100);
  const columnCount = 6 + (showBpsColumn ? 1 : 0) + (showCommissionPercentageColumn ? 1 : 0);
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = items.filter(({
      loan
    }) => needle.length === 0 ? true : loan.borrower.toLowerCase().includes(needle) || loan.id.toLowerCase().includes(needle));
    const direction = descending ? -1 : 1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'loanAmount') return (a.loan.amount - b.loan.amount) * direction;
      if (sortKey === 'bps') return ((a.bps ?? 0) - (b.bps ?? 0)) * direction;
      if (sortKey === 'commissionPercentage') return (a.loan.commissionPercentage - b.loan.commissionPercentage) * direction;
      if (sortKey === 'commission') return (a.commission - b.commission) * direction;
      return a.loan.fundedDate.localeCompare(b.loan.fundedDate) * direction;
    });
  }, [items, query, sortKey, descending]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const start = currentPage * PAGE_SIZE;
  const visibleRows = hideControls ? rows : rows.slice(start, start + PAGE_SIZE);
  const showPagination = !hideControls && rows.length > PAGE_SIZE;
  const footerVolume = rows.reduce((sum, row) => sum + row.loan.amount, 0);
  const filteredCommission = rows.reduce((sum, row) => sum + row.commission, 0);
  const footerCommission = query.trim() ? filteredCommission : totals.net;
  function handleSort(key: SortKey) {
    setPage(0);
    if (key === sortKey) {
      setDescending(value => !value);
      return;
    }
    setSortKey(key);
    setDescending(key !== 'fundedDate');
  }
  function sortMark(key: SortKey) {
    if (key !== sortKey) return '↕';
    return descending ? '↓' : '↑';
  }
  const sortButton = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => <button type="button" onClick={() => handleSort(key)} className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition hover:text-brand-700 ${key === sortKey ? 'text-brand-700' : 'text-slate-500'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-312" data-mp="1bf8g67gwnpo9">
      {label}
      <span aria-hidden="true" className={key === sortKey ? '' : 'text-slate-300'} data-id="element-313" data-mp="sohb3i3yalfo">
        {sortMark(key)}
      </span>
    </button>;
  return <section aria-labelledby="loans-heading" className="rounded-xl border border-slate-200 bg-white shadow-card" data-id="element-314" data-mp="npnvnk53skcv">
      <div className="flex flex-col gap-4 border-b border-slate-200 p-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-315" data-mp="iruooovz7mbj">
        <div data-id="element-316" data-mp="9pd4sh3sn5rp">
          <h2 id="loans-heading" className="text-base font-bold text-slate-900" data-id="element-317" data-mp="9plk0fne2n62">
            {heading}
          </h2>
          {subtitle ? <p className="mt-0.5 text-sm text-slate-500" data-id="element-318" data-mp="29snu4les2gy6">{subtitle}</p> : null}
        </div>
        {hideControls ? null : <label className="relative" data-id="element-319" data-mp="2dsenmwvovb6e">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-320" data-mp="2021fouisrwh2" />
            <input type="search" value={query} onChange={event => {
          setQuery(event.target.value);
          setPage(0);
        }} placeholder="Search borrower or loan #" aria-label="Search payment line items" className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 lg:w-72" data-id="element-321" data-mp="24yjk1ta7tssv" />
          </label>}
      </div>

      <div className="overflow-x-auto" data-id="element-322" data-mp="3gg1eg25wpg2q">
        <table className="w-full min-w-[760px] text-left text-sm" data-id="element-323" data-mp="3gf01tt98sliw">
          <thead data-id="element-324" data-mp="3qjq8gcflcdtg">
            <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-325" data-mp="3qj6qwwturrfy">
              <th scope="col" className="px-6 py-3" data-id="element-326" data-mp="3lmgau9i1svuj">
                Borrower
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-327" data-mp="38gt7f9qii9o">
                Loan #
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-328" data-mp="3uks0a876hnle">
                Lender Portal
              </th>
              <th scope="col" className="px-6 py-3" data-id="element-329" data-mp="d3iicb40fkc9">
                {sortButton('fundedDate', 'Funded')}
              </th>
              <th scope="col" className="px-6 py-3 text-right" data-id="element-330" data-mp="88361nlnhwhy">
                {sortButton('loanAmount', 'Loan amount', 'right')}
              </th>
              {showBpsColumn ? <th scope="col" className="px-6 py-3 text-right" data-id="element-331" data-mp="i4y3e366tqun">
                  {sortButton('bps', 'BPS', 'right')}
                </th> : null}
              {showCommissionPercentageColumn ? <th scope="col" className="px-6 py-3 text-right" data-id="element-332" data-mp="2jxm7sfukikc0">
                  {sortButton('commissionPercentage', 'Commission %', 'right')}
                </th> : null}
              <th scope="col" className="px-6 py-3 text-right" data-id="element-333" data-mp="ru8fpsb1vsi4">
                {sortButton('commission', 'Commission', 'right')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-334" data-mp="11tr0gaysbib8">
            {visibleRows.length === 0 ? <tr data-id="element-335" data-mp="15t8737py6lhz">
                <td colSpan={columnCount} className="px-6 py-14 text-center" data-id="element-336" data-mp="15roovbkqe0dk">
                  <p className="text-sm font-semibold text-slate-700" data-id="element-337" data-mp="1ar9rbvz40qv1">
                    {items.length === 0 ? 'No payment line items in this period' : 'No loans match your search'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-338" data-mp="1khvzj54hyf3p">
                    {items.length === 0 ? 'Loans appear here as soon as they fund.' : 'Try a different borrower name or loan number.'}
                  </p>
                </td>
              </tr> : visibleRows.map(item => {
            const {
              loan
            } = item;
            const showBadges = item.overrideApplied;
            return <tr key={loan.id} className="transition hover:bg-slate-50" data-id="element-339" data-mp="351ho11z35kym">
                    <th scope="row" className="px-6 py-4 font-semibold" data-id="element-340" data-mp="302ennuu1w2wz">
                      <a href={`#/loans/${loan.id}`} onClick={event => event.preventDefault()} className="text-brand-700 underline decoration-brand-200 decoration-2 underline-offset-2 transition hover:text-brand-900 hover:decoration-brand-500" data-id="element-341" data-mp="1jpzxyo2ll5p">
                        {loan.borrower}
                      </a>
                      {showBadges ? <span className="mt-1 flex flex-wrap gap-1.5" data-id="element-342" data-mp="kc1zkdnca8c5">
                          {item.overrideApplied ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800" data-id="element-343" data-mp="fho2m6t13t5d">
                              Override
                            </span> : null}
                        </span> : null}
                    </th>
                    <td className="px-6 py-4 tabular-nums text-slate-500" data-id="element-344" data-mp="3f1z1kgh1ng2x">{loan.id}</td>
                    <td className="px-6 py-4 text-slate-700" data-id="element-345" data-mp="6mxa0tlt1436">
                      {loan.lenderPortalUrl ? <a href={loan.lenderPortalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-brand-700 transition hover:text-brand-900" data-id="element-346" data-mp="bm0f2e1sbab5">
                          {loan.lenderPortal}
                          <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-347" data-mp="2nbb1z8v0qird" />
                        </a> : loan.lenderPortal}
                    </td>
                    <td className="px-6 py-4 tabular-nums text-slate-700" data-id="element-348" data-mp="2arnm420k7i6e">{formatDate(loan.fundedDate)}</td>
                    <td className="px-6 py-4 text-right tabular-nums text-slate-900" data-id="element-349" data-mp="2or5fkuosvjm3">{formatCurrency(loan.amount)}</td>
                    {showBpsColumn ? <td className="px-6 py-4 text-right" data-id="element-350" data-mp="1w3jbzmfkoez8">
                        <span className="inline-flex items-center justify-end gap-2" data-id="element-351" data-mp="1w42hu4ry7cww">
                          <span className={`tabular-nums ${item.overrideApplied ? 'font-semibold text-amber-800' : 'text-slate-700'}`} data-id="element-352" data-mp="1r6arvgav60vc">
                            {item.bps !== null ? formatBps(item.bps) : '—'}
                          </span>
                          {canEditBps && !hideControls ? <button type="button" onClick={() => onEditBps(item)} aria-label={`Edit BPS override for ${loan.borrower}`} className="rounded-md border border-slate-300 px-2 py-0.5 text-xs font-semibold text-slate-600 transition hover:border-brand-300 hover:text-brand-700" data-id="element-353" data-mp="18eqehljaxz2i">
                              Edit
                            </button> : null}
                        </span>
                      </td> : null}
                    {showCommissionPercentageColumn ? <td className="px-6 py-4 text-right tabular-nums text-slate-700" data-id="element-354" data-mp="7c9wn4himstm">{loan.commissionPercentage}%</td> : null}
                    <td className="px-6 py-4 text-right font-bold tabular-nums text-slate-900" data-id="element-355" data-mp="2ernpe3i0dlm">
                      {formatCurrency(item.commission)}
                    </td>
                  </tr>;
          })}
          </tbody>
          {rows.length > 0 ? <tfoot data-id="element-356" data-mp="24zc3w6w36xrp">
              <tr className="border-t border-slate-200 bg-slate-50 text-sm" data-id="element-357" data-mp="24zu9vv8jwqct">
                <th scope="row" colSpan={4} className="px-6 py-4 text-left font-bold text-slate-900" data-id="element-358" data-mp="290e2qmm3d7t5">
                  {rows.length} {rows.length === 1 ? role.unitNoun : role.unitNounPlural}
                </th>
                <td className="px-6 py-4 text-right font-bold tabular-nums text-slate-900" data-id="element-359" data-mp="2izd5jok6e988">
                  {formatCurrency(footerVolume)}
                </td>
                {showBpsColumn ? <td data-id="element-360" data-mp="1qc923o0qe2yk" /> : null}
                {showCommissionPercentageColumn ? <td data-id="element-361" data-mp="1vakdw4al6njf" /> : null}
                <td className="px-6 py-4 text-right font-bold tabular-nums text-brand-800" data-id="element-362" data-mp="1v99aw6oytjtd">
                  {formatCurrency(footerCommission)}
                </td>
              </tr>
              {totals.capped && !query.trim() ? <tr className="bg-slate-50 text-xs" data-id="element-363" data-mp="12jiwdwjf8fwq">
                  <td colSpan={columnCount} className="px-6 pb-4 text-right font-medium text-slate-500" data-id="element-364" data-mp="17jy6mf8ep7ah">
                    {formatCurrency(totals.gross)} earned, capped at {formatCurrency(role.monthlyCap ?? 0)}
                  </td>
                </tr> : null}
            </tfoot> : null}
        </table>
      </div>

      {showPagination ? <div aria-label="Loan table pagination" className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-6 py-4" data-id="element-365" data-mp="14aqsv98vr86y">
          <p className="text-sm text-slate-500" data-id="element-366" data-mp="gn8vmy6ujhwl">
            Showing {start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of {rows.length}
          </p>
          <div className="flex gap-2" data-id="element-367" data-mp="qkdhewws5bcv">
            <button type="button" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-368" data-mp="qh951c95v9b4">
              Previous
            </button>
            <button type="button" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= pageCount - 1} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-369" data-mp="3ez5g65wwsaqi">
              Next
            </button>
          </div>
        </div> : null}
    </section>;
}